import '~/assets/styles/index.scss';

import React, { useEffect } from 'react';
import { GeoLocationProvider } from '~/app/context';
import { CookiePreferencesPanel } from '~/widgets/consent/cookie-preferences-panel';
import { getConsent, onConsentChanged } from '~/shared/consent/consent-manager';
import { onLegalLinkClick } from '~/shared/hooks/sourcePhone';

const ATTRIBUTION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'msclkid',
  'cq_cmp',
  'cq_con',
  'cq_med',
  'cq_net',
  'cq_plt',
  'cq_src',
  'cq_term',
  'city',
];
const FIRST_TOUCH_KEY = 'analytics_first_touch_v1';
const LAST_TOUCH_KEY = 'analytics_last_touch_v1';
const ATTR_COOKIE_PREFIX = 'ctm_attr_';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 90;

const readAttribution = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  const url = new URL(window.location.href);
  const result = {};

  ATTRIBUTION_KEYS.forEach((key) => {
    const value = url.searchParams.get(key);
    if (value) {
      result[key] = value;
    }
  });

  result.landing_url = url.href;
  result.landing_path = `${url.pathname}${url.search}`;
  result.referrer = document.referrer || '';

  return result;
};

const hasPaidParams = (attribution) => {
  return Boolean(
    attribution.gclid ||
      attribution.msclkid ||
      attribution.fbclid ||
      attribution.utm_source ||
      attribution.cq_src ||
      attribution.cq_cmp
  );
};

const setCookie = (name, value, maxAge = COOKIE_MAX_AGE) => {
  if (typeof document === 'undefined') {
    return;
  }
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax`;
};

const setAttributionCookies = (firstTouch, lastTouch) => {
  const writeObject = (prefix, source) => {
    Object.entries(source || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        return;
      }
      setCookie(`${ATTR_COOKIE_PREFIX}${prefix}_${key}`, String(value));
    });
  };

  writeObject('first', firstTouch);
  writeObject('last', lastTouch);
};

const persistAttribution = (attribution) => {
  try {
    const firstTouchRaw = window.localStorage.getItem(FIRST_TOUCH_KEY);
    if (!firstTouchRaw) {
      window.localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(attribution));
    }
    window.localStorage.setItem(LAST_TOUCH_KEY, JSON.stringify(attribution));
  } catch (error) {
    // no-op
  }
};

const readPersistedAttribution = () => {
  try {
    const firstTouch = JSON.parse(window.localStorage.getItem(FIRST_TOUCH_KEY) || '{}');
    const lastTouch = JSON.parse(window.localStorage.getItem(LAST_TOUCH_KEY) || '{}');
    return { firstTouch, lastTouch };
  } catch (error) {
    return { firstTouch: {}, lastTouch: {} };
  }
};

const primeDataLayer = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  const currentAttribution = readAttribution();
  persistAttribution(currentAttribution);
  const { firstTouch, lastTouch } = readPersistedAttribution();
  // These cookies feed marketing/ad tooling (CTM), so they follow ads consent.
  if (getConsent().ads) {
    setAttributionCookies(firstTouch, lastTouch);
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'traffic_context_ready',
    page_url: window.location.href,
    page_path: `${window.location.pathname}${window.location.search}`,
    referrer: document.referrer || '',
    source_data: {
      first_touch: firstTouch,
      last_touch: lastTouch,
    },
    first_touch: firstTouch,
    last_touch: lastTouch,
  });

  return currentAttribution;
};

const runCtmWhenReady = () => {
  if (typeof window === 'undefined') {
    return;
  }

  // CallTrackingMetrics has no Consent Mode support of its own — it must be
  // gated explicitly. It is only started once ads consent is granted (and is
  // re-attempted from the consent-change listener below if the user later
  // opts in via the cookie preferences panel).
  if (!getConsent().ads) {
    return;
  }

  const maxAttempts = 20;
  let attempts = 0;

  const tick = () => {
    attempts += 1;
    if (window.__ctm?.main?.runNow) {
      window.__ctm.main.runNow();
      return;
    }
    if (attempts < maxAttempts) {
      setTimeout(tick, 500);
    }
  };

  tick();
};

const loadGtm = () => {
  if (typeof window === 'undefined') {
    return;
  }
  if (window.__gtmLoaded) {
    return;
  }

  const gtmId = process.env.GTM_CODE;
  if (!gtmId) {
    return;
  }

  window.__gtmLoaded = true;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;
  document.head.appendChild(script);
};

const HEADER_OFFSET = 100;

// Anything the visitor does on their own ends the correction below; otherwise it
// keeps pulling the page back to the footer, e.g. over the scroll-to-top button.
const USER_SCROLL_INTENT_EVENTS = ['pointerdown', 'touchstart', 'wheel', 'keydown'];

let cancelHashScroll = () => {};

// The call-button notice links scroll down to the footer legal block. Images
// below the fold keep loading while the smooth scroll runs and push the footer
// further down, so the position is corrected until it stops moving.
const scrollToHash = (id) => {
  cancelHashScroll();

  let settled = 0;
  let timeoutId = null;
  let cancelled = false;

  const cancel = () => {
    cancelled = true;
    clearTimeout(timeoutId);
    USER_SCROLL_INTENT_EVENTS.forEach((type) => window.removeEventListener(type, cancel));
  };

  cancelHashScroll = cancel;
  USER_SCROLL_INTENT_EVENTS.forEach((type) => window.addEventListener(type, cancel, { passive: true }));

  const step = (attempt) => {
    if (cancelled) {
      return;
    }

    const element = document.getElementById(id);
    if (!element) {
      cancel();
      return;
    }

    const top = Math.max(0, element.getBoundingClientRect().top + window.pageYOffset - HEADER_OFFSET);
    if (Math.abs(top - window.pageYOffset) < 4) {
      settled += 1;
    } else {
      settled = 0;
      window.scrollTo({ top, behavior: attempt === 0 ? 'smooth' : 'auto' });
    }

    if (settled < 2 && attempt < 20) {
      timeoutId = setTimeout(() => step(attempt + 1), attempt === 0 ? 600 : 150);
    } else {
      cancel();
    }
  };

  step(0);
};

const ThirdPartyDeferredLoader = () => {
  useEffect(() => {
    // GTM now starts from <head> (gatsby-ssr.js), so loadGtm() below is only a
    // fallback. Swap again once React has hydrated: hydration can put the
    // static numbers back over a swap CTM made while the page was parsing.
    runCtmWhenReady();

    let idleId = null;
    let timeoutId = null;
    let attribution = {};

    const triggerLoad = () => {
      loadGtm();
      runCtmWhenReady();
      window.removeEventListener('scroll', triggerLoad);
      window.removeEventListener('pointerdown', triggerLoad);
      window.removeEventListener('keydown', triggerLoad);
      if (idleId && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };

    const scheduleThirdParty = () => {
      window.addEventListener('scroll', triggerLoad, { once: true, passive: true });
      window.addEventListener('pointerdown', triggerLoad, { once: true, passive: true });
      window.addEventListener('keydown', triggerLoad, { once: true });

      if (hasPaidParams(attribution)) {
        timeoutId = setTimeout(triggerLoad, 50);
      } else if (typeof window.requestIdleCallback === 'function') {
        idleId = window.requestIdleCallback(triggerLoad, { timeout: 3500 });
      } else {
        timeoutId = setTimeout(triggerLoad, 3500);
      }
    };

    const init = () => {
      attribution = primeDataLayer();
      scheduleThirdParty();
    };

    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(init, { timeout: 200 });
    } else {
      timeoutId = setTimeout(init, 0);
    }

    const onPhoneClick = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      const anchor = target.closest('a[href^="tel:"]');
      if (!anchor) {
        return;
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'phone_click',
        phone_href: anchor.getAttribute('href') || '',
      });
      triggerLoad();
    };

    const onHashLinkClick = (event) => {
      const link = event.target?.closest?.('a[href^="#"]');
      const href = link?.getAttribute('href');
      if (!href || href === '#') {
        return;
      }
      const id = decodeURIComponent(href.slice(1));
      if (!document.getElementById(id)) {
        return;
      }
      event.preventDefault();
      scrollToHash(id);

      // Skip links target a focusable region (tabindex="-1"); move focus there
      // too, or the next Tab would start from the top of the page again.
      const destination = document.getElementById(id);
      if (destination.getAttribute('tabindex') === '-1') {
        destination.focus({ preventScroll: true });
      }
    };

    document.addEventListener('click', onPhoneClick);
    document.addEventListener('click', onHashLinkClick);
    document.addEventListener('click', onLegalLinkClick);
    document.addEventListener('auxclick', onLegalLinkClick);

    // If the user grants ads consent later (via the cookie preferences
    // panel), start CTM at that point instead of waiting for another
    // interaction — GTM itself is unaffected since Consent Mode already
    // covers the Google tags inside it.
    const unsubscribeConsent = onConsentChanged((event) => {
      if (event.detail?.ads) {
        const { firstTouch, lastTouch } = readPersistedAttribution();
        setAttributionCookies(firstTouch, lastTouch);
        runCtmWhenReady();
      }
    });

    return () => {
      window.removeEventListener('scroll', triggerLoad);
      window.removeEventListener('pointerdown', triggerLoad);
      window.removeEventListener('keydown', triggerLoad);
      if (idleId && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      document.removeEventListener('click', onPhoneClick);
      document.removeEventListener('click', onHashLinkClick);
      document.removeEventListener('click', onLegalLinkClick);
      document.removeEventListener('auxclick', onLegalLinkClick);
      unsubscribeConsent();
    };
  }, []);

  return null;
};

export const wrapRootElement = ({ element }) => {
  return (
    <GeoLocationProvider>
      <ThirdPartyDeferredLoader />
      <CookiePreferencesPanel />
      {element}
    </GeoLocationProvider>
  );
};
